import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, MapPin, Phone, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { RatingLine } from "../../components/Stars";
import PublicFrame, { usePublicBranding } from "./PublicFrame";

function Details({ j }) {
  return (
    <div className="rounded-[var(--radius)] border border-emerald-500/30 bg-emerald-500/5 p-5 space-y-2 text-sm" data-testid="job-accepted-details">
      <div className="flex items-center gap-2 font-semibold text-emerald-800"><CheckCircle2 className="h-5 w-5" />You accepted this visit</div>
      <div><b data-testid="job-client-name">{j.client_name}</b></div>
      <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /><span data-testid="job-address">{j.address}, {j.postcode}</span></div>
      {j.client_phone && <a className="flex items-center gap-2 text-primary" href={`tel:${j.client_phone}`} data-testid="job-client-phone"><Phone className="h-4 w-4" />{j.client_phone}</a>}
      {j.notes && <p className="text-muted-foreground whitespace-pre-wrap">{j.notes}</p>}
    </div>
  );
}

export default function JobOffer() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [declined, setDeclined] = useState(false);
  useEffect(() => { api.get(`/public/job/${token}`).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  usePublicBranding(data);
  const act = async (kind) => {
    setBusy(true);
    try {
      const r = await api.post(`/public/job/${token}/${kind}`);
      if (kind === "accept") { setData(r.data); toast.success("Job accepted"); } else setDeclined(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (err) return <PublicFrame><p className="py-12 text-center text-muted-foreground" data-testid="job-error">{err}</p></PublicFrame>;
  if (!data) return <PublicFrame><p>…</p></PublicFrame>;
  const j = data.job;
  const want = params.get("action");
  const expired = j.job_expires_at && new Date(j.job_expires_at) < new Date();
  if (declined) return <PublicFrame branding={data.branding}><div className="py-16 text-center space-y-3" data-testid="job-declined"><XCircle className="h-12 w-12 text-muted-foreground mx-auto" /><h1 className="text-3xl">Thanks for letting us know</h1><p className="text-muted-foreground">We'll offer this visit to another practitioner.</p></div></PublicFrame>;
  return (
    <PublicFrame branding={data.branding}>
      <div className="space-y-6 max-w-xl" data-testid="job-offer">
        <div><p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Home-visit request</p><h1 className="text-3xl sm:text-4xl" data-testid="job-service">{j.service_name}</h1></div>
        <div className="rounded-[var(--radius)] border bg-card p-5 space-y-2 text-sm">
          <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /><b data-testid="job-when">{j.date} at {j.start_time}</b> · {j.duration} min</div>
          <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />Area <b data-testid="job-area">{j.area}</b></div>
          <div className="flex items-center gap-2">Patient rating <RatingLine rating={j.patient_rating} count={j.patient_rating_count} testId="job-patient-rating" /></div>
        </div>
        {j.job_status === "accepted" && <Details j={j} />}
        {j.job_status === "offered" && j.status !== "cancelled" && (expired
          ? <p className="text-sm text-muted-foreground" data-testid="job-expired">This request has expired and is being offered to another practitioner.</p>
          : <div className="space-y-3">
            <p className="text-sm text-muted-foreground" data-testid="job-deadline">Please respond by {new Date(j.job_expires_at).toLocaleString()}.{want && " Tap to confirm."}</p>
            <div className="grid grid-cols-2 gap-3">
              <Button className={`rounded-full h-12 ${want === "accept" ? "ring-4 ring-primary/30" : ""}`} data-testid="job-accept-btn" disabled={busy} onClick={() => act("accept")}>Accept</Button>
              <Button variant="outline" className={`rounded-full h-12 ${want === "decline" ? "ring-4 ring-primary/30" : ""}`} data-testid="job-decline-btn" disabled={busy} onClick={() => act("decline")}>Decline</Button>
            </div>
          </div>)}
        {!["offered", "accepted"].includes(j.job_status) && <p className="text-sm text-muted-foreground" data-testid="job-closed">This job is no longer open.</p>}
      </div>
    </PublicFrame>
  );
}
